/**
 * What the room said about itself, as the panel reads it. Pure, so jest can
 * check the wording without a DOM.
 */
import type {
  BoardroomAssignment,
  BoardroomDirectorNote,
  BoardroomResearchPlan,
  BoardroomRun,
} from '../../../types';
import { seatRole } from '../plainWords';

/** The opening plan's one-line tally: "5 hunches · 4 searches for players · 5 for
 *  complaints · demand numbers". Null while it is still being written or if it
 *  failed — a plan nobody can act on is not worth a line. */
export function planTally(plan: BoardroomResearchPlan | null | undefined): string | null {
  if (!plan || plan.status !== 'ready') return null;
  const bits: string[] = [];
  const hunches = plan.hunches?.length ?? 0;
  // The plan's JSON key is still `hunches`; what it carries is the room's
  // opening QUESTIONS.
  if (hunches) bits.push(`${hunches} ${hunches === 1 ? 'question' : 'questions'}`);
  const count = (c: string) => (plan.searches ?? []).filter(s => s.check === c).length;
  const players = count('players');
  const pains = count('pains');
  if (players) bits.push(`${players} ${players === 1 ? 'search' : 'searches'} for players`);
  if (pains) bits.push(`${pains} for complaints`);
  if (count('demand')) bits.push('demand numbers');
  return bits.length ? bits.join(' · ') : null;
}

/** What the room noticed about its own shape, as a phrase. */
const GAP_LABELS: Record<string, string> = {
  ORPHANS: 'things hanging on nothing',
  DISAGREEMENT: 'the tables disagree',
  STALLED: 'work going nowhere',
  THIN: 'not enough on the board',
};

/** The plan to show: the run's own while it has one, else the room's latest.
 *  A plan is written on an empty board, so only a room's first dig carries
 *  one — reading only the run lost the questions on every dig after. */
export function readyPlan(
  run: BoardroomRun | null,
  roomPlan?: BoardroomResearchPlan | null,
): BoardroomResearchPlan | null {
  if (run?.plan?.status === 'ready') return run.plan;
  return roomPlan?.status === 'ready' ? roomPlan : null;
}

/** The first sentence of a paragraph, for a one-line summary. */
export function firstSentence(text: string | null | undefined): string {
  const t = (text ?? '').trim().replace(/\s+/g, ' ');
  const m = t.match(/^.*?[.!?](?=\s|$)/);
  return m ? m[0] : t;
}

export interface DirectorHeadline {
  gist: string;
  chips: { text: string; warn?: boolean }[];
}

/** The line above the board: the director's own read if it has one, else what
 *  the plan set out to learn; then what is worth opening the slideout for —
 *  a frame change waiting on you before anything else.
 *  Null when there is nothing to say — the line is not drawn at all. */
export function directorHeadline(
  run: BoardroomRun | null,
  assignments: BoardroomAssignment[],
  frameWaiting = 0,
  /** The room's latest plan. Defaults to the run's own; a later dig has none. */
  roomPlan?: BoardroomResearchPlan | null,
): DirectorHeadline | null {
  const note = run?.director_note ?? null;
  const plan = readyPlan(run, roomPlan);
  const live = groupAssignments(assignments);
  if (!note && !plan && live.length === 0 && !frameWaiting) return null;
  const gist =
    firstSentence(note?.state) ||
    firstSentence(plan?.summary) ||
    (live.length ? 'worklists handed out' : 'the setup has a change waiting for you');
  const chips: DirectorHeadline['chips'] = [];
  // A DECISION WAITING comes first: it is the one thing here that needs you.
  if (frameWaiting) {
    chips.push({ text: `frame change waiting${frameWaiting > 1 ? ` (${frameWaiting})` : ''}`, warn: true });
  }
  if (note?.observation && note.gap_kind && note.gap_kind !== 'NONE') {
    chips.push({ text: GAP_LABELS[note.gap_kind] ?? 'something looks off', warn: true });
  }
  const q = plan?.questions?.length ?? 0;
  if (q) chips.push({ text: `${q} ${q === 1 ? 'question' : 'questions'}` });
  if (live.length) chips.push({ text: `${live.length} ${live.length === 1 ? 'worklist' : 'worklists'}` });
  return { gist, chips };
}

/** A note written before the last few turns landed is describing a board that
 *  has moved. Say so rather than presenting it as current. */
export function noteIsStale(note: BoardroomDirectorNote | null | undefined, run: BoardroomRun | null): boolean {
  if (!note || !run) return false;
  return typeof note.turns_at === 'number' && (run.turns_done ?? 0) > note.turns_at;
}

/** "3 of 6 rounds" — and nothing once it has stopped reading, because a
 *  counter that has finished counting is just furniture. */
export function roundsLabel(run: BoardroomRun | null, max: number): string {
  const n = run?.director_rounds ?? 0;
  if (!n) return '';
  return n >= max ? 'done reading' : `${n} of ${max} rounds`;
}

/** The one line a worklist gets in the panel. */
export function assignmentLine(a: BoardroomAssignment): string {
  const n = a.item_ids?.length ?? 0;
  const what = a.what ? a.what.replace(/_/g, ' ') : 'work';
  return `${seatRole(a.archetype)} · ${n} ${n === 1 ? 'item' : 'items'} · ${what}`;
}

/** Live worklists, the ones being worked first: what is happening now sits
 *  above what is queued. */
export function groupAssignments(rows: BoardroomAssignment[]): BoardroomAssignment[] {
  const rank: Record<string, number> = { taken: 0, pending: 1, done: 2, superseded: 3, dropped: 4 };
  return rows
    .filter(a => a.status === 'taken' || a.status === 'pending')
    .slice()
    .sort((a, b) => (rank[a.status] ?? 9) - (rank[b.status] ?? 9) || a.archetype.localeCompare(b.archetype));
}

/** What the room noticed about its own shape, in a phrase. `NONE` is an
 *  answer — "I looked and the shape is sound" — not an absence. */
export function gapWords(note: BoardroomDirectorNote | null | undefined): string | null {
  if (!note?.observation || note.gap_kind === 'NONE') return null;
  const label = note.gap_kind ? GAP_LABELS[note.gap_kind] : null;
  return label ? `${label} - ${note.observation}` : note.observation;
}
